import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import {
  getUnavailableTimeSlotsApi,
  addUnavailableTimeSlotApi,
  removeUnavailableTimeSlotApi,
  removeAllUnavailableTimeSlotsForDateApi,
} from "../../Apis/unavailableTimeSlots/unavailableTimeSlots";
import { useToast } from "../../Hooks/useToast";

const CELL_SIZE = 50;

const formatDateSafe = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const UnavailableTimeSlots = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [unavailableTimeSlots, setUnavailableTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dialogError, setDialogError] = useState("");

  const { toastSuccess, toastError } = useToast();

  const loadUnavailableTimeSlots = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getUnavailableTimeSlotsApi();
      setUnavailableTimeSlots(response.unavailableTimeSlots || []);

      if (response.error && response.source === "fallback_due_to_error") {
        toastError(response.error);
      }

      return response.unavailableTimeSlots || [];
    } finally {
      setIsLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    loadUnavailableTimeSlots();
  }, [loadUnavailableTimeSlots]);

  const handleDateClick = (date) => {
    setSelectedDate(formatDateSafe(date));
    setStartTime(null);
    setEndTime(null);
    setDialogError("");
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (isUpdating) return;
    setIsDialogOpen(false);
    setDialogError("");
    setStartTime(null);
    setEndTime(null);
  };

  const handleAddTimeSlot = async () => {
    if (!selectedDate || !startTime || !endTime) {
      setDialogError("Bitte wählen Sie eine Start- und Endzeit aus.");
      return;
    }

    if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) {
      setDialogError("Bitte wählen Sie gültige Uhrzeiten aus.");
      return;
    }

    const startTimeFormatted = format(startTime, "HH:mm");
    const endTimeFormatted = format(endTime, "HH:mm");

    if (startTimeFormatted >= endTimeFormatted) {
      setDialogError("Die Endzeit muss nach der Startzeit liegen.");
      return;
    }

    try {
      setIsUpdating(true);
      setDialogError("");

      const response = await addUnavailableTimeSlotApi(
        selectedDate,
        startTimeFormatted,
        endTimeFormatted
      );

      if (!response.success) {
        const message = response.error || "Die Zeit konnte nicht hinzugefügt werden.";
        setDialogError(message);
        toastError(message);
        return;
      }

      await loadUnavailableTimeSlots();
      toastSuccess(`Zeit gespeichert: ${startTimeFormatted} - ${endTimeFormatted}`);
      handleDialogClose();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveTimeSlot = async (date, timeSlotId) => {
    try {
      setIsUpdating(true);
      const response = await removeUnavailableTimeSlotApi(date, timeSlotId);

      if (!response.success) {
        toastError(response.error || "Die Zeit konnte nicht entfernt werden.");
        return;
      }

      await loadUnavailableTimeSlots();
      toastSuccess("Zeit erfolgreich entfernt.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveAllTimeSlotsForDate = async (date) => {
    try {
      setIsUpdating(true);
      const response = await removeAllUnavailableTimeSlotsForDateApi(date);

      if (!response.success) {
        toastError(response.error || "Die Zeiten konnten nicht entfernt werden.");
        return;
      }

      await loadUnavailableTimeSlots();
      toastSuccess("Alle Zeiten für dieses Datum wurden entfernt.");
    } finally {
      setIsUpdating(false);
    }
  };

  const nextMonth = () => setCurrentMonth(addDays(endOfMonth(currentMonth), 1));
  const prevMonth = () => setCurrentMonth(addDays(startOfMonth(currentMonth), -1));

  const hasUnavailableTimeSlots = (date) => {
    const dateFormatted = formatDateSafe(date);
    const entry = unavailableTimeSlots.find((item) => item.date === dateFormatted);
    return Boolean(entry?.timeSlots?.length);
  };

  const renderHeader = () => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <IconButton onClick={prevMonth} disabled={isUpdating}>
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" fontWeight={600}>
        {format(currentMonth, "MMMM yyyy")}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={loadUnavailableTimeSlots}
          disabled={isLoading || isUpdating}
          size="small"
          title="Daten neu laden"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={nextMonth} disabled={isUpdating}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(new Date(day));
      day = addDays(day, 1);
    }

    return (
      <Grid container spacing={1} justifyContent="flex-start">
        {days.map((dayItem) => {
          if (!isSameMonth(dayItem, monthStart)) return null;

          const hasUnavailable = hasUnavailableTimeSlots(dayItem);
          const isToday = isSameDay(dayItem, new Date());

          return (
            <Grid item key={dayItem.toString()}>
              <Box
                sx={{
                  width: CELL_SIZE,
                  height: CELL_SIZE,
                  borderRadius: "50%",
                  bgcolor: isToday ? "#caa96a" : hasUnavailable ? "#ffebee" : "#ffffff",
                  border: hasUnavailable ? "2px solid #f44336" : "1px solid #e0e0e0",
                  color: hasUnavailable ? "#f44336" : "black",
                  fontWeight: isToday ? "bold" : hasUnavailable ? "bold" : "normal",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: isUpdating ? "wait" : "pointer",
                  opacity: isUpdating ? 0.6 : 1,
                  "&:hover": {
                    backgroundColor: isToday ? "#caa96a" : hasUnavailable ? "#ffcdd2" : "#f5f5f5",
                  },
                }}
                onClick={() => !isUpdating && handleDateClick(dayItem)}
              >
                <Typography variant="body2">{format(dayItem, "d")}</Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderUnavailableTimeSlotsList = () => {
    const entriesWithTimeSlots = unavailableTimeSlots.filter((entry) => entry.timeSlots?.length > 0);

    if (entriesWithTimeSlots.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            Keine aktiven nicht verfügbaren Zeiten.
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
            Klicken Sie auf einen Tag im Kalender, um Zeiten hinzuzufügen.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          {entriesWithTimeSlots.map((dateEntry) => (
            <Grid item xs={12} sm={6} md={4} key={dateEntry.date}>
              <Card
                sx={{
                  height: "100%",
                  border: "2px solid #f44336",
                  backgroundColor: "#fff5f5",
                  "&:hover": {
                    boxShadow: 3,
                    transform: "translateY(-2px)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CalendarTodayIcon sx={{ color: "#f44336", mr: 1, fontSize: "1.2rem" }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color="#f44336">
                        {format(parseISO(dateEntry.date), "dd.MM.yyyy")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {dateEntry.timeSlots.length} Zeitslot{dateEntry.timeSlots.length !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="caption" fontWeight={600} color="#d32f2f" sx={{ mb: 1, display: "block" }}>
                    Nicht verfügbare Zeiten:
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {dateEntry.timeSlots.map((timeSlot, index) => {
                      const timeSlotId =
                        timeSlot._id || `${dateEntry.date}-${index}-${timeSlot.startTime}-${timeSlot.endTime}`;

                      return (
                        <Chip
                          key={timeSlotId}
                          icon={<AccessTimeIcon sx={{ fontSize: "0.8rem !important" }} />}
                          label={`${timeSlot.startTime} - ${timeSlot.endTime}`}
                          onDelete={() => handleRemoveTimeSlot(dateEntry.date, timeSlotId)}
                          disabled={isUpdating}
                          size="small"
                          sx={{
                            backgroundColor: "#f44336",
                            color: "white",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            height: "24px",
                            opacity: isUpdating ? 0.6 : 1,
                            "& .MuiChip-icon": {
                              color: "white",
                              fontSize: "0.8rem",
                            },
                            "& .MuiChip-deleteIcon": {
                              color: "white",
                              fontSize: "0.9rem",
                              "&:hover": {
                                color: "#ffcdd2",
                              },
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </CardContent>

                <CardActions sx={{ pt: 0, pb: 2, px: 2, justifyContent: "space-between" }}>
                  <Typography variant="caption" color="#e65100" sx={{ fontSize: "0.7rem" }}>
                    Für Kunden nicht verfügbar
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveAllTimeSlotsForDate(dateEntry.date)}
                    disabled={isUpdating}
                    title="Alle Zeiten für dieses Datum löschen"
                    sx={{
                      backgroundColor: "#ffebee",
                      "&:hover": { backgroundColor: "#ffcdd2" },
                      width: 28,
                      height: 28,
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {isUpdating && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Lade nicht verfügbare Zeiten...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body1" color="black" fontWeight={500} mb={2} gutterBottom>
        Nicht verfügbare Zeiten verwalten
      </Typography>

      {renderHeader()}
      {renderCalendar()}

      <Typography variant="body1" color="black" fontWeight={500} mt={4} gutterBottom>
        Aktuelle nicht verfügbare Zeiten:
      </Typography>
      {renderUnavailableTimeSlotsList()}

      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nicht verfügbare Zeit hinzufügen
          {selectedDate && (
            <Typography variant="body2" color="textSecondary">
              Datum: {format(parseISO(selectedDate), "dd.MM.yyyy")}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TimePicker
                  label="Startzeit"
                  value={startTime}
                  onChange={(newValue) => {
                    setStartTime(newValue);
                    setDialogError("");
                  }}
                  ampm={false}
                  minutesStep={15}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label="Endzeit"
                  value={endTime}
                  minTime={startTime || undefined}
                  onChange={(newValue) => {
                    setEndTime(newValue);
                    setDialogError("");
                  }}
                  ampm={false}
                  minutesStep={15}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              {dialogError && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 2,
                      color: "#b42318",
                      backgroundColor: "#fef3f2",
                      border: "1px solid #fecdca",
                      fontSize: "0.9rem",
                    }}
                  >
                    {dialogError}
                  </Box>
                </Grid>
              )}
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isUpdating}>
            Abbrechen
          </Button>
          <Button
            onClick={handleAddTimeSlot}
            variant="contained"
            disabled={isUpdating || !startTime || !endTime}
            startIcon={isUpdating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {isUpdating ? "Wird hinzugefügt..." : "Hinzufügen"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnavailableTimeSlots;
